import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { DataTableDirective } from 'angular-datatables';
import { FlashMessagesService } from 'flash-messages-angular';
import * as _ from 'lodash';
import * as moment from 'moment';
import { Subject } from 'rxjs';
import { DatatablesHelper, RouteHelper, TextHelper } from 'src/app/helpers';
import { AdminService } from 'src/app/services/admin.service';
import { AuthService } from 'src/app/services/auth.service';
import { UserService } from 'src/app/services/user.service';
import { UploadUsersDialogComponent } from '../shared/dialogs/upload-users-dialog/upload-users-dialog.component';
import { IUsersQueryParams } from './users.viewmodel';
import { Clipboard } from '@angular/cdk/clipboard';
import { RouteService } from 'src/app/core/services';

@Component({
  selector: 'app-users',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.css']
})
export class UsersComponent implements OnInit, OnDestroy {
  readonly COLS_HIDDEN_BY_DEFAULT = [0];

  users: any[] = [];
  loaded = false;

  agencyID?: string;
  agency?: any;

  userID?: string;
  progressID?: string;

  routeParams: IUsersQueryParams = {};

  dtOptions: DataTables.Settings = {};

  // We use this trigger because fetching the list of users can take a while,
  // thus we ensure the data is fetched before rendering
  dtTrigger: Subject<any> = new Subject<any>();

  @ViewChild(DataTableDirective, { static: false })
  dtElement!: DataTableDirective;

  constructor(
    public auth: AuthService,
    private userService: UserService,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private adminService: AdminService,
    private flashMessage: FlashMessagesService,
    private clipboard: Clipboard,
    private router: Router,
    private routeService: RouteService
  ) { }

  ngOnInit(): void {
    this.loadRouteParams();
    this.initTable();
    this.loadUsers();
  }

  ngOnDestroy(): void {
    // Do not forget to unsubscribe the event
    this.dtTrigger.unsubscribe();
  }

  private loadRouteParams(): void {
    const queryParams = this.route.snapshot.queryParams as IUsersQueryParams;

    this.routeParams = RouteHelper.map(queryParams, {
      agencyid: String
    });

    this.agencyID = this.routeParams.agencyid;
  }

  private initTable(): void {
    this.dtOptions = {
      pagingType: 'full_numbers',
      pageLength: 50,
      // enables horizontal scrolling on datatable
      scrollX: true,

      // Declare the use of the extension in the dom parameter and give a class for the css (can't get the css to apply though so commented out)
      // dom: '<"table-buttons"lBfr>tip',
      dom: 'lBfrtip',

      columnDefs: [
        // Indexes of columns to be hidden by default
        { targets: [0], visible: false }
      ],
    };

    // Configure the buttons
    Object.assign(this.dtOptions, {
      buttons: [
        'colvis',
        'excel'
      ]
    });
  }

  private async loadUsers() {
    const query = {
      agencyid:    this.agencyID || '',
      getagency:   true,
      getprogress: true
    };

    const res: any = await this.userService.getAll(query).toPromise();

    this.agency = res.agency;
    this.users = res.users;

    this.loadTable();

    this.toggleStatusCol();
  }

  private loadTable() {
    if (this.loaded) {
      this.rerender();
    }
    else {
      // Calling the DT trigger to manually render the table
      this.dtTrigger.next();
      this.loaded = true;
    }
  }

  private toggleStatusCol(show?: boolean) {
    const showStatus = show !== undefined ? show : this.users.some(user => user.banned);

    const colsDef = this.dtOptions.columnDefs![0];
    const colsHidden = this.COLS_HIDDEN_BY_DEFAULT;

    colsDef.targets = showStatus ? _(colsHidden).without(0).value() : colsHidden;
  }

  uploadUsers(agency: any, course: string) {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    // Open dialog
    const dialogRef = this.dialog.open(UploadUsersDialogComponent, dialogConfig);

    dialogRef.afterClosed().toPromise().then(async (res: any) => {
      if (!res || !res.users.length) return;

      this.routeService.scrollToTop();

      const agencyId = agency ? agency._id || this.agencyID : null;
      const users = res.users;

      // Register Users
      const data: any = await this.adminService.registerMany(course, agencyId, users).toPromise();

      // Reload table data
      await this.loadUsers().catch(() => {});

      // Success
      if (data.success) {
        // Display success
        this.flashMessage.show('Users registered.', {
          cssClass: 'alert-success',
          timeout: 3000
        });
      }
      // Error
      else {
        // Display error
        this.flashMessage.show('There was an error saving users', {
          cssClass: 'alert-danger',
          timeout: 3000
        });
      }
    });
  }

  private rerender() {
    DatatablesHelper.redrawTable(this.dtElement, this.dtTrigger);
  }

  async deleteUser(index: number) {
    const user = this.users[index];

    // Assign userID
    this.userID = user._id;

    if (confirm(`Are you sure you want to permanently delete user ${user.email} and associated data?`) === false) {
      return;
    }

    // Delete User
    const data: any = await this.adminService.deleteUser(user._id).toPromise();

    // Reload table data
    await this.loadUsers().catch(() => {});

    // Success
    if (data.success) {
      // Display success
      this.flashMessage.show("User deleted.", {
        cssClass: 'alert-success',
        timeout: 3000
      });
    }
  }

  async banUser(index: number) {
    const user = this.users[index];
    const email = user.email;

    // Assign userID
    this.userID = user._id;

    if (confirm(`Are you sure you want to ban user ${user.email}?`) === false) {
      return;
    }

    // Ban User
    const data: any = await this.adminService.banUser(email).toPromise();

    // // Reload table data
    // await this.loadUsers().catch(() => {});

    // Success
    if (data.success) {
      user.banned = true;
      this.toggleStatusCol(true);
      this.rerender();

      // Display success
      this.flashMessage.show("User banned.", {
        cssClass: 'alert-success',
        timeout: 3000
      });
    }
  }

  async unbanUser(index: number) {
    const user = this.users[index];
    const email = user.email;

    // Assign userID
    this.userID = user._id;

    // Unban User
    const data: any = await this.adminService.unbanUser(email).toPromise();

    // // Reload table data
    // await this.loadUsers().catch(() => {});

    // Success
    if (data.success) {
      user.banned = false;

      // Display success
      this.flashMessage.show("User unbanned.", {
        cssClass: 'alert-success',
        timeout: 3000
      });
    }
  }

  async deleteCourse(index: number) {
    const user = this.users[index];

    // Assign userID
    this.userID = user._id;
    this.progressID = user.progressID;

    if (!this.userID) {
      return;
    }

    const agencyId = this.agencyID || null;

    // Delete Course
    const data: any = await this.adminService.deregisterCourses(agencyId, this.userID).toPromise();

    // Reload table data
    await this.loadUsers().catch(() => {});

    // Success
    if (data.success) {
      // Display success
      this.flashMessage.show("Course completion deleted.", {
        cssClass: 'alert-success',
        timeout: 3000
      });

    }
  }

  async copyGeoJsonLink() {
    const link = await this.adminService.getGeoJsonLink('users').toPromise();

    await TextHelper.copy(link, this.clipboard);

    // Success
    this.flashMessage.show("Link copied to clipboard.", {
      cssClass: 'alert-success',
      timeout: 3000
    });
  }

  async copyGeoRssLink() {
    const link = await this.adminService.getGeoRssLink('users').toPromise();

    await TextHelper.copy(link, this.clipboard);

    // Success
    this.flashMessage.show("Link copied to clipboard.", {
      cssClass: 'alert-success',
      timeout: 3000
    });
  }

  async loginUnder(email: string) {
    const data = await this.adminService.impersonate('user', email).toPromise();

    // User authenticated, store token
    if (data.success) {
      this.auth.storeUserData(data.token, data.user);
      this.auth.storeAdminStatus(false);

      this.router.navigate(['/courses']);

      // Display log in to user
      this.flashMessage.show("You have been logged in under " + email, {
        cssClass: 'alert-success',
        timeout: 3000
      });
    }
  }

  sortDate(value: any) {
    return value ? moment(value).startOf('day').unix() : 0;
  }
}
